/**
 * My info's field rules, shared by the Mini App's edit sheet and the website's
 * Profile edited in place (web-account-pages D5): the four reserved fields, the
 * API's length caps, the email check, the US phone mask, and Save's gate (Save
 * only when something changed and every field is valid). All four stay required,
 * as registration requires them (Decision 2). The client-side mirror of the
 * server's reserved-field rules, so the PATCH never answers 400.
 */
import { formatUsPhoneWithCountry } from '@/components/forms/formatPhone';
import type { ProfileMyInfo } from '@/types/profile';

export interface MyInfoValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export type MyInfoField = keyof MyInfoValues;

export const MY_INFO_MAX_LENGTH = {
  firstName: 100,
  lastName: 100,
  email: 254,
  phone: 32,
} as const;

/** The fields as the edit starts: the saved values, the phone in its mask. */
export function myInfoFrom(initial: ProfileMyInfo): MyInfoValues {
  return {
    firstName: initial.firstName ?? '',
    lastName: initial.lastName ?? '',
    email: initial.email ?? '',
    phone: formatUsPhoneWithCountry(initial.phone ?? ''),
  };
}

/** A field's typed value as it is kept: the phone takes its mask as typed. */
export function myInfoInput(key: MyInfoField, raw: string): string {
  return key === 'phone' ? formatUsPhoneWithCountry(raw) : raw;
}

export function isMyInfoDirty(values: MyInfoValues, initial: ProfileMyInfo): boolean {
  const saved = myInfoFrom(initial);
  return (Object.keys(saved) as MyInfoField[]).some((key) => values[key] !== saved[key]);
}

export function isValidEmail(value: string): boolean {
  const email = value.trim();
  if (email.length === 0 || email.length > MY_INFO_MAX_LENGTH.email) return false;
  const parts = email.split('@');
  if (parts.length !== 2) return false;
  const [local, domain] = parts;
  if (!local || local.length > 64 || local.startsWith('.') || local.endsWith('.') || local.includes('..')) return false;
  if (!/^[A-Z0-9.!#$%&'*+/=?^_`{|}~-]+$/i.test(local)) return false;
  const labels = domain.split('.');
  if (labels.length < 2) return false;
  return labels.every(
    (label) =>
      label.length > 0 &&
      label.length <= 63 &&
      !label.startsWith('-') &&
      !label.endsWith('-') &&
      /^[A-Z0-9-]+$/i.test(label),
  );
}

/** The email field's error while what is typed is not an email; null otherwise. */
export function myInfoEmailError(email: string): string | null {
  return email.length > 0 && !isValidEmail(email) ? 'Enter a valid email address.' : null;
}

/** Every field filled, within the caps, and the email valid. */
export function isMyInfoValid(values: MyInfoValues): boolean {
  const filled = values.firstName.trim() && values.lastName.trim() && values.email.trim() && values.phone.trim();
  const withinLimits = (Object.keys(MY_INFO_MAX_LENGTH) as MyInfoField[]).every((key) => values[key].length <= MY_INFO_MAX_LENGTH[key]);
  return Boolean(filled && isValidEmail(values.email) && withinLimits);
}

/** What the save sends: each field trimmed. */
export function myInfoToSave(values: MyInfoValues): MyInfoValues {
  return {
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    email: values.email.trim(),
    phone: values.phone.trim(),
  };
}
